import React from "react";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { useSidebarOptional } from "@/components/ui/sidebar";

/**
 * CSS variable the pinned bar sets on the document root to its own height. The
 * app shell's `SidebarInset` pads its bottom by it, so the last fields and any
 * content after the form scroll clear of the bar.
 */
export const FORM_ACTION_BAR_SPACE_VAR = "--form-action-bar-space";

// Height per mounted pinned bar; the shell padding follows the tallest.
const reservedHeights = new Map<symbol, number>();

function syncReservedSpace() {
  const style = document.documentElement.style;
  if (reservedHeights.size === 0) {
    style.removeProperty(FORM_ACTION_BAR_SPACE_VAR);
    return;
  }
  style.setProperty(
    FORM_ACTION_BAR_SPACE_VAR,
    `${Math.max(...reservedHeights.values())}px`,
  );
}

/** One entry of the error summary: a field the user can jump to. */
export interface FormErrorSummaryItem {
  key: string;
  /** The field's label, so the summary reads as a list of places to fix. */
  label: React.ReactNode;
  /** Focuses the field; omitted for an error no field owns. */
  onSelect?: () => void;
}

export interface FormActionBarProps {
  /**
   * Pins the bar to the bottom edge of the content area (default true):
   * `position: fixed` from the sidebar's right edge to the inset's right edge.
   * Inside a dialog or alert dialog it pins to the dialog's scroller instead
   * (`sticky bottom-0`). Sheets, dialog footers owned by the dialog and inline
   * cards pass `pinned={false}`.
   */
  pinned?: boolean;
  /** Secondary actions rendered first in the actions group, before Delete. */
  leading?: React.ReactNode;
  /**
   * Error summary entries; renders the destructive Alert when non-empty. A
   * string is a form-level message, an item a field link.
   */
  errors?: Array<string | FormErrorSummaryItem>;
  onDelete?: () => void | Promise<void>;
  deleteLabel?: React.ReactNode;
  deleteDisabled?: boolean;
  isDeleting?: boolean;
  /** Omitted -> no Cancel button is rendered (never a no-op Cancel). */
  onCancel?: () => void;
  cancelLabel?: React.ReactNode;
  cancelDisabled?: boolean;
  cancelTestId?: string;
  /** Omitted -> Submit is type="submit"; given -> type="button" calling it. */
  onSubmit?: () => void;
  /** Omitted -> no Submit button is rendered. */
  submitLabel?: React.ReactNode;
  submitDisabled?: boolean;
  /** Shows common.saving in place of submitLabel and disables Submit. */
  isSubmitting?: boolean;
  submitTestId?: string;
  className?: string;
}

/**
 * The one action row of a form (#1684): error summary on the left, then the
 * actions in the fixed order leading slot, Delete (`mr-auto`), Cancel, Submit —
 * which is also the tab order.
 *
 * A pinned bar (the default) sits at the bottom edge of the content area at all
 * times while its form is mounted, also when the form is shorter than the
 * viewport (D1 amended). It stays in the form's DOM, so Enter-to-submit and the
 * tab order are unchanged; only its box is `fixed`. `print:static` puts it back
 * into the document flow when printing.
 */
export function FormActionBar({
  pinned = true,
  leading,
  errors,
  onDelete,
  deleteLabel,
  deleteDisabled,
  isDeleting,
  onCancel,
  cancelLabel,
  cancelDisabled,
  cancelTestId,
  onSubmit,
  submitLabel,
  submitDisabled,
  isSubmitting,
  submitTestId,
  className,
}: FormActionBarProps) {
  const { i18n } = useLingui();
  const sidebar = useSidebarOptional();
  const barRef = React.useRef<HTMLDivElement>(null);
  // A `fixed` box inside a dialog would take the dialog's transform as its
  // containing block and scroll away with the content, so a bar inside a
  // dialog keeps the sticky footer that pins to the dialog's own scroller.
  const [placement, setPlacement] = React.useState<"viewport" | "dialog">("viewport");

  React.useLayoutEffect(() => {
    if (barRef.current?.closest('[role="dialog"], [role="alertdialog"]')) {
      setPlacement("dialog");
    }
  }, []);

  const onViewport = pinned && placement === "viewport";

  React.useLayoutEffect(() => {
    const el = barRef.current;
    if (!onViewport || !el) return;
    const key = Symbol("form-action-bar");
    const measure = () => {
      reservedHeights.set(key, el.offsetHeight);
      syncReservedSpace();
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
      reservedHeights.delete(key);
      syncReservedSpace();
    };
  }, [onViewport]);

  // Placement against the app shell's inset variant (components/ui/sidebar.tsx):
  // expanded, the inset starts at --sidebar-width with no left margin; collapsed
  // to icons, it starts after the icon rail's gap (--sidebar-width-icon + 1rem)
  // plus its 0.5rem left margin. The frame paints the inset's sidebar-coloured
  // right and bottom margin so the bar reads as the inset's rounded bottom edge.
  // Below md the sidebar is a Sheet and the inset has no margin: full width.
  const onDesktopShell = sidebar !== null && !sidebar.isMobile;
  const frameClass = cn(
    "fixed bottom-0 right-0 z-20 transition-[left] duration-200 ease-linear print:static print:bg-transparent print:p-0",
    !onDesktopShell && "left-0",
    onDesktopShell && "bg-sidebar pb-2 pr-2",
    onDesktopShell && sidebar.state === "expanded" && "left-[--sidebar-width]",
    onDesktopShell &&
      sidebar.state === "collapsed" &&
      "left-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))] pl-2",
  );

  // An unpinned bar with a className gets no default padding, so the sheet
  // footer keeps exactly the class string it had before this component existed.
  const variantClass = !pinned
    ? className
      ? undefined
      : "p-4"
    : placement === "dialog"
      ? "p-4 sticky bottom-0 z-20 print:static"
      : cn("p-4", onDesktopShell && "rounded-b-xl print:rounded-none");

  const hasActions =
    Boolean(leading) ||
    Boolean(onDelete) ||
    Boolean(onCancel) ||
    submitLabel !== undefined;

  const bar = (
    <div
      ref={barRef}
      role="group"
      aria-label={i18n._({ id: "common.actions", message: "Actions" })}
      data-testid="form-action-bar"
      data-pinned={pinned ? "true" : "false"}
      data-placement={pinned ? placement : undefined}
      className={cn(
        "border-t bg-background flex items-center justify-between gap-4",
        variantClass,
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        {errors && errors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              {i18n._(msg({ message: "Please fix the following errors:" }))}
              {/* Capped so a long list scrolls inside the bar instead of
                  pushing the bar over the form on a short viewport. */}
              <ul className="mt-1 flex max-h-20 flex-wrap gap-x-4 gap-y-1 overflow-y-auto">
                {errors.map((error, index) =>
                  typeof error === "string" ? (
                    <li key={`message-${index}`}>{error}</li>
                  ) : (
                    <li key={error.key}>
                      {error.onSelect ? (
                        <button
                          type="button"
                          className="rounded-sm font-medium underline underline-offset-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={error.onSelect}
                        >
                          {error.label}
                        </button>
                      ) : (
                        error.label
                      )}
                    </li>
                  ),
                )}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </div>

      {hasActions && (
        // self-end keeps a page bar's buttons in the bottom band when a tall
        // error summary grows the bar, so the floating feedback button above
        // the bar stays clear of them.
        <div
          className={cn(
            "flex flex-wrap items-center justify-end gap-2",
            onViewport && "self-end",
          )}
        >
          {leading}
          {onDelete && (
            <Button
              type="button"
              variant="destructive"
              className="mr-auto"
              onClick={onDelete}
              disabled={deleteDisabled || isDeleting}
            >
              {isDeleting
                ? i18n._({ id: "common.deleting", message: "Deleting..." })
                : (deleteLabel ??
                  i18n._({ id: "common.delete", message: "Delete" }))}
            </Button>
          )}
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={cancelDisabled}
              data-testid={cancelTestId}
            >
              {cancelLabel ?? i18n._({ id: "common.cancel", message: "Cancel" })}
            </Button>
          )}
          {submitLabel !== undefined && (
            <Button
              type={onSubmit ? "button" : "submit"}
              onClick={onSubmit}
              disabled={submitDisabled || isSubmitting}
              data-testid={submitTestId}
            >
              {isSubmitting
                ? i18n._({ id: "common.saving", message: "Saving..." })
                : submitLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  if (!onViewport) return bar;
  return (
    <div data-testid="form-action-bar-frame" className={frameClass}>
      {bar}
    </div>
  );
}
